import { Component, inject, Input, OnChanges, OnDestroy, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';
import { Merchant } from '@olv/ui-shared/models/merchant.model';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { MerchantEditFormComponent } from './merchant-edit-form/merchant-edit-form.component';
import { MerchantUserManagementComponent } from '../merchant-user-management/merchant-user-management.component';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';

/**
 * Shows the merchant the details page already loaded. It does not fetch the merchant itself: GET api/Merchant/{id}
 * is the slow request this page is built around, so a change made here asks the details page to reload it, once.
 */
@Component({
  selector: 'app-merchant-edit',
  standalone: true,
  imports: [MerchantEditFormComponent, MerchantUserManagementComponent],
  templateUrl: './merchant-edit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./merchant-edit.component.scss'],
})
export class MerchantEditComponent implements OnChanges, OnDestroy {
  @Input() merchant?: Merchant;
  logoBase64?: string;
  closeEdit = false;

  private destroy$ = new Subject<void>();

  private toastr = inject(ToastService);
  private merchantService = inject(MerchantService);

  get merchantId(): string {
    return this.merchant?.id ?? '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['merchant'] && this.merchant?.logo?.fileData) {
      this.logoBase64 = this.merchant.logo.fileData;
    }
  }

  /** The details page owns the merchant and reloads it, and this tab receives the result through its input. */
  onMerchantChanged(): void {
    if (this.merchantId) {
      this.merchantService.triggerMerchantUpdate(this.merchantId);
    }
  }

  /**
   * retrieve the base64 payload
   */
  onLogoUpdated(mediaId: string) {
    this.merchantService
      .getFileData(mediaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (base64) => {
          this.logoBase64 = base64.fileData;
          // Update the shared signal
          this.merchantService.setLogo(base64.fileData);
          this.toastr.success('Logo updated successfully');
        },
        error: (err) => {
          console.error('Failed to fetch updated logo:', err);
        },
      });
  }
  onExpandRight(expand: boolean) {
    this.closeEdit = expand;
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
